import {
  RecipeState,
  RecipeGroupState,
  RecipeColumnState,
  RecipeRowState,
  VisibilityOperator,
  Visibility,
} from './types';
import type { GroupStore } from './stores/GroupStore';
import type { RowStore } from './stores/RowStore';
import { FormulaScope as PlainFormulaScope } from './FormulaScope';
import { log } from '@/lib/utils/logger';

function isRowStore(row: RecipeRowState | RowStore): row is RowStore {
  return typeof (row as RowStore).setValue === 'function';
}

// Helper function to compare values based on visibility operator
export const compare = (value: any, operator: VisibilityOperator, compareValue: string): boolean => {
  switch (operator) {
    case VisibilityOperator.EQ:
      // Handle boolean values specially
      if (typeof value === 'boolean') {
        if (compareValue.toLowerCase() === 'true') return value === true;
        if (compareValue.toLowerCase() === 'false') return value === false;
      }
      // Handle string comparison first
      if (typeof value === 'string' && typeof compareValue === 'string') {
        return value === compareValue;
      }
      // Handle numeric comparison
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      const numCompareValue = parseFloat(compareValue);
      return numValue === numCompareValue;
    case VisibilityOperator.NE:
      // Handle boolean values specially
      if (typeof value === 'boolean') {
        if (compareValue.toLowerCase() === 'true') return value !== true;
        if (compareValue.toLowerCase() === 'false') return value !== false;
      }
      // Handle string comparison first
      if (typeof value === 'string' && typeof compareValue === 'string') {
        return value !== compareValue;
      }
      // Handle numeric comparison
      const numValue2 = typeof value === 'string' ? parseFloat(value) : value;
      const numCompareValue2 = parseFloat(compareValue);
      return numValue2 !== numCompareValue2;
    case VisibilityOperator.IN:
      return compareValue
        .split(',')
        .map((v) => v.trim())
        .includes(String(value));
    case VisibilityOperator.NOT_IN:
      return !compareValue
        .split(',')
        .map((v) => v.trim())
        .includes(String(value));
    case VisibilityOperator.CONTAINS:
      if (value == null || value === '') {
        return false;
      }
      if (typeof value === 'string' && value.includes(',')) {
        return value.split(',').some((part) => part.trim() === compareValue);
      }
      return String(value) === compareValue || String(value).includes(compareValue);
    default:
      log.warn(`[visibilityUtils] Unknown visibility operator: ${operator}, default to visible.`);
      return true;
  }
};

// Helper function to check if any row in a group matches a visibility condition
const checkAnyRowMatchesCondition = (
  group: RecipeGroupState,
  columnSymbol: string,
  operator: VisibilityOperator,
  compareValue: string
): boolean => {
  // For visibility: if ANY row meets the condition, the column becomes relevant
  for (const row of group.rows) {
    const value = row.values.get(columnSymbol);
    if (value !== undefined && value !== null && value !== '') {
      if (compare(value, operator, compareValue)) {
        return true; // Found a row that matches the condition
      }
    }
  }
  return false; // No row matches the condition
};

// Function to evaluate column visibility based on visibility type and conditions
export const evaluateColumnVisibility = (
  column: RecipeColumnState,
  recipeState: RecipeState,
  /** MobX stores/FormulaScope (use mobxGroup) or legacy recipe/FormulaScope */
  formulaScope: unknown,
  row: RecipeRowState | RowStore,
  readonly: boolean,
  /** Required for visibility formulas with MobX FormulaScope (see stores/FormulaScope.apply). */
  mobxGroup?: GroupStore
): boolean => {
  switch (column.visibility) {
    case Visibility.Always:
      return true;
    case Visibility.OnlyForUpload:
      return false; // Not in upload mode
    case Visibility.Never:
      return false;
    case Visibility.IfModifiable:
      return !readonly;
    case Visibility.IfReadonly:
      return readonly;
    case Visibility.IfCondition:
      // Handle formula-based visibility
      if (column.visibilityFormula) {
        try {
          const scopeAny = formulaScope as {
            apply?: (g: GroupStore, r: RowStore) => { evaluateFormula: (f: string, r: RowStore) => unknown };
            evaluateFormula?: (f: string, r: RecipeRowState | RowStore) => unknown;
          };
          let result: unknown;
          if (mobxGroup && isRowStore(row) && typeof scopeAny.apply === 'function') {
            result = scopeAny.apply(mobxGroup, row).evaluateFormula(column.visibilityFormula, row);
          } else if (typeof scopeAny.evaluateFormula === 'function') {
            result = scopeAny.evaluateFormula(column.visibilityFormula, row);
          } else {
            log.warn(
              '[visibilityUtils] visibilityFormula set but formula scope has no evaluateFormula; defaulting visible'
            );
            return true;
          }
          const isVisible = compare(
            result,
            column.visibilityOp || VisibilityOperator.EQ,
            column.visibilityVal || 'true'
          );
          log.debug('[visibilityUtils] Visibility formula evaluation:', () => ({
            formula: column.visibilityFormula,
            result,
            operator: column.visibilityOp,
            compareValue: column.visibilityVal,
            isVisible,
          }));
          return isVisible;
        } catch (error) {
          log.warn('[visibilityUtils] Error evaluating visibility formula, defaulting to visible:', {
            formula: column.visibilityFormula,
            error: error instanceof Error ? error.message : String(error),
          });
          return true;
        }
      }

      // Handle column reference visibility
      if (column.refColumn) {
        try {
          const parts = column.refColumn.split('.');
          const operator = column.visibilityOp || VisibilityOperator.EQ;
          const compareValue = column.visibilityVal || 'true';

          if (parts.length === 2) {
            // Cross-group reference: "groupSymbol.columnSymbol"
            const [groupSymbol, columnSymbol] = parts;
            const group = recipeState.groups.find((g) => g.symbol === groupSymbol);
            if (group) {
              return checkAnyRowMatchesCondition(group, columnSymbol, operator, compareValue);
            }
            return false; // Group not found
          } else if (parts.length === 1) {
            // Same-group reference: "columnSymbol" - check if current row matches
            const columnSymbol = parts[0];
            const value = row.values.get(columnSymbol);
            return compare(value, operator, compareValue);
          }

          return true; // Invalid refColumn format, default to visible
        } catch (error) {
          log.warn('[visibilityUtils] Error evaluating visibility column reference, defaulting to visible:', {
            refColumn: column.refColumn,
            error: error instanceof Error ? error.message : String(error),
          });
          return true;
        }
      }

      return true; // Default to visible if no condition specified
    default:
      return true; // Default to visible for unknown visibility types
  }
};

/**
 * Whether an IfCondition column should appear in the group header (any row satisfies the rule).
 * Used for plain {@link RecipeGroupState} in readonly mode when MobX is not available.
 *
 * @param recipeState Full recipe (all groups). Required for cross-group {@link RecipeColumnState.refColumn}
 *   (e.g. `precursors.used`) and {@link RecipeColumnState.visibilityFormula} (e.g. `group("precursors", "used", "S")`).
 * @param plainFormulaScope Optional reusable {@link PlainFormulaScope} for formula-based rules; created when omitted.
 */
export function isIfConditionColumnVisibleOnGroup(
  column: RecipeColumnState,
  group: RecipeGroupState,
  readonly: boolean,
  recipeState: RecipeState,
  plainFormulaScope?: PlainFormulaScope
): boolean {
  if (column.visibility !== Visibility.IfCondition) {
    return false;
  }
  const scope = plainFormulaScope ?? new PlainFormulaScope(() => recipeState);
  return group.rows.some((row) => {
    const scoped = scope.apply(group).apply(row);
    return evaluateColumnVisibility(column, recipeState, scoped, row, readonly);
  });
}
